/** @format */

import React, { memo } from "react";
import { Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { NavLink, useParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { useLocale } from "../i18n/hooks";
import { useDispatch } from "react-redux";
import {
  lessonProgress,
  selectLessonsProgress,
} from "../lessonProgress/processSlice";
import { difficultyLevel, Lesson, Sublesson } from "../resources/types";
import { setShowSidebar } from "../sidebar/sidebarSlice";
import FormattedText from "../formattedText/FormattedText";
import "./SidebarLesson.scss";

type Props = {
  lesson: Lesson;
  index: number;
  sidebarVisible: boolean;
};

const SidebarLesson: React.FC<Props> = ({ lesson, index, sidebarVisible }) => {
  const locale = useLocale();
  const { t: _ } = useTranslation();
  const lessonsProgress = useAppSelector(selectLessonsProgress());
  const { lessonId: activeLesson } = useParams();

  const progress: Number =
    lessonsProgress.find(
      (lessonProgress: lessonProgress) =>
        lessonProgress.lessonUUID === lesson.uuid,
    )?.progress || 0;
  const subLessonCount: Number = lesson.subLessons.length;
  const dispatch = useDispatch();

  const lessonTitle =
    typeof lesson.title === "object" ? lesson.title?.[locale] : lesson?.title;

  return (
    <Accordion.Item
      as="div"
      eventKey={index.toString()}
      className={`lesson bg-white border-0`}
      key={index}
    >
      <Accordion.Header
        as="div"
        className={`lesson-header d-flex p-3 ${
          index === parseInt(activeLesson ?? "") && "active-lesson"
        }`}
      >
        <div className="d-flex gap-3 lesson-info fw-semibold">
          <span className="lesson-marker">{index + 1}.0</span>
          <span>
            <FormattedText html={lessonTitle ?? ""} />
          </span>
        </div>
        <div className="ms-2 d-flex align-items-center">
          {lesson.difficultyLevels &&
            lesson.difficultyLevels.map((level: difficultyLevel, idx) => {
              let label = "";
              switch (level) {
                case "foundation":
                  label = _("Foundation tier");
                  break;
                case "intermediate":
                  label = _("Intermediate tier");
                  break;
                case "higher":
                  label = _("Higher tier");
                  break;
              }
              return (
                <span
                  key={idx}
                  className={`level-${level} level-marker position-relative border border-white rounded-circle opacity-75`}
                  title={label}
                ></span>
              );
            })}
        </div>
        <div className="d-flex align-items-center ms-auto">
          <div
            className={`badge ${
              progress === subLessonCount ? "bg-success" : "bg-warning"
            } mx-3 fs-6 px-2`}
            aria-label={subLessonCount.toString()}
          >
            {subLessonCount.toString()}
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body as="div" className="p-0">
        {lesson.subLessons.map((subLesson: Sublesson, pageIndex) => {
          const subLessonTitle =
            typeof subLesson.title === "object"
              ? subLesson?.title[locale]
              : subLesson?.title;

          return (
            <NavLink
              title={subLessonTitle}
              key={pageIndex}
              to={`${index}/${pageIndex}`}
              role="link"
              className="sublesson"
              onClick={() => {
                window.innerWidth <= 1024
                  ? dispatch(setShowSidebar(false))
                  : dispatch(setShowSidebar(true));
              }}
            >
              <FormattedText html={subLessonTitle ?? ""} />
            </NavLink>
          );
        })}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default memo(SidebarLesson);
